import { authHeaders, fetchWithAuthRetry, getApiUrl, parseApiError } from './common';
import type { Attachment, AttachmentTargetType } from '../../types/attachment';

type PresignedUploadUrlResponse = {
  data: { uploadUrl: string; key: string };
};

type AttachmentListResponse = {
  data: Attachment[];
  meta: { total: number };
};

type AttachmentResponse = {
  data: Attachment;
};

type DownloadUrlResponse = {
  data: { downloadUrl: string; expiresInSeconds: number };
};

export type DraftUploadUrlInput = {
  fileName: string;
  contentType: string;
  size: number;
  targetType?: AttachmentTargetType;
};

export type CreateAttachmentInput = {
  targetType: AttachmentTargetType;
  targetId: string;
  key: string;
  originalName: string;
};

export const getDraftUploadUrl = async (
  token: string,
  input: DraftUploadUrlInput,
): Promise<{ uploadUrl: string; key: string }> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/attachments/draft-upload-url`, {
    method: 'POST',
    headers: authHeaders(token, true),
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to create draft attachment upload URL: ${response.status}`);
  }

  const payload = (await response.json()) as PresignedUploadUrlResponse;
  return payload.data;
};

// 대상 타입 → 첨부 목록 조회 엔드포인트. 서버 라우트와 1:1로 매핑한다.
const attachmentListPath = (targetType: AttachmentTargetType, targetId: string): string => {
  switch (targetType) {
    case 'daemonTrigger':
      return `/api/daemon-triggers/${targetId}/attachments`;
    case 'codeReview':
      return `/api/code-reviews/${targetId}/attachments`;
    case 'completionReport':
      return `/api/completion-reports/${targetId}/attachments`;
    case 'document':
      return `/api/documents/${targetId}/attachments`;
  }
};

export const getTargetAttachments = async (
  token: string,
  targetType: AttachmentTargetType,
  targetId: string,
): Promise<Attachment[]> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}${attachmentListPath(targetType, targetId)}`, {
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch attachments: ${response.status}`);
  }

  const payload = (await response.json()) as AttachmentListResponse;
  return payload.data;
};

export const getAttachments = (token: string, daemonTriggerId: string): Promise<Attachment[]> =>
  getTargetAttachments(token, 'daemonTrigger', daemonTriggerId);

export const createAttachment = async (token: string, input: CreateAttachmentInput): Promise<Attachment> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/attachments`, {
    method: 'POST',
    headers: authHeaders(token, true),
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to create attachment: ${response.status}`);
  }

  const payload = (await response.json()) as AttachmentResponse;
  return payload.data;
};

export const deleteAttachment = async (token: string, id: string): Promise<void> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/attachments/${id}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to delete attachment: ${response.status}`);
  }
};

export const getDownloadUrl = async (
  token: string,
  id: string,
): Promise<{ downloadUrl: string; expiresInSeconds: number }> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/attachments/${id}/download-url`, {
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch attachment download URL: ${response.status}`);
  }

  const payload = (await response.json()) as DownloadUrlResponse;
  return payload.data;
};

export const uploadFileToPresignedUrl = (
  uploadUrl: string,
  file: File,
  onProgress?: (progress: number) => void,
  contentType = file.type,
): Promise<void> => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        onProgress?.(Math.round((event.loaded / event.total) * 100));
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress?.(100);
        resolve();
        return;
      }
      reject(new Error(`Failed to upload file: ${xhr.status}`));
    };
    xhr.onerror = () => reject(new Error('Failed to upload file.'));
    xhr.send(file);
  });
};
